import type { MatchmakingV2AdminRestartInstancePayload } from '@playcastdotio/Messaging';

/**
 * Dependencies of the admin restart-instance handler. Structural subset of the
 * coordinator/service graph — keeps this handler testable without init.ts.
 */
export type AdminRestartInstanceDeps = {
  /** Active android stream id (GameInstanceLaunchManager); undefined after teardown / on non-android launches. */
  getActiveStreamId: () => string | undefined;
  /** True when the game instance store holds a record for the index. */
  hasLiveInstance: (gameInstanceIndex: number) => boolean;
  /**
   * InstanceRecoveryManager: issue a serialized, guarded intentional restart.
   * This marks the record `restarting`, dedups against an in-flight restart,
   * and issues the single `restartInstance` through the global one-at-a-time
   * queue — so a manual admin restart can never race or stack on an auto one.
   */
  requestIntentionalRestart: (gameInstanceIndex: number) => void;
};

/**
 * Production-readiness Phase 5 (R5 host half): consume the API's
 * `MATCHMAKING_V2_ADMIN_RESTART_INSTANCE` push and restart one game instance
 * on the active android stream.
 *
 * One file, one message. The production binding is assembled in
 * `coordinators/init.ts` (append-only) and registered in the matchmakingV2
 * namespace dispatcher (`handlers/matchmakingV2.ts`).
 *
 * The restart mirrors the dev-controls path (`GameInstanceDevControls`): a
 * single `requestIntentionalRestart(index)` into InstanceRecoveryManager. That
 * marks the record `restarting` (so a concurrent `instanceClosed` is expected
 * and the policy does not fight the restart), dedups against an in-flight
 * restart, and issues the one `restartInstance(streamId, index)` through the
 * global one-at-a-time queue. The relaunch echo (or its timeout) settles the
 * outcome; a failed intentional restart becomes `failed` and the policy takes
 * over.
 *
 * Defense-in-depth: the API already validated the index, so an unknown index
 * or a missing active stream is logged and ignored — never an error.
 */
export const createAdminRestartInstanceHandler = (deps: AdminRestartInstanceDeps) => {
  return (payload: MatchmakingV2AdminRestartInstancePayload): void => {
    const { gameInstanceIndex, reason } = payload;

    const streamId = deps.getActiveStreamId();
    if (!streamId) {
      console.warn(
        `[AdminRestartInstance-Handler] Ignoring admin restart for instance ${gameInstanceIndex} — ` +
        'no active android stream',
      );
      return;
    }

    if (!deps.hasLiveInstance(gameInstanceIndex)) {
      console.warn(
        `[AdminRestartInstance-Handler] Ignoring admin restart for unknown instance ` +
        `${gameInstanceIndex} (stream "${streamId}") — no live store record`,
      );
      return;
    }

    console.log('[AdminRestartInstance-Handler] Admin restart requested:', {
      streamId,
      gameInstanceIndex,
      reason: reason ?? null,
    });

    // Route through the recovery manager's serialized intentional-restart entry
    // point: it marks the record `restarting`, dedups against any in-flight
    // restart for this index, issues the single `restartInstance` through the
    // global one-at-a-time queue, and settles the outcome via the relaunch echo
    // (or its timeout, which hands a failed restart to the recovery policy).
    deps.requestIntentionalRestart(gameInstanceIndex);
  };
};
